import React from 'react'
import { GoodsOwner } from '../models/shared/GoodsOwner'
import { OngoingInvoice } from '../models/shared/OngoingInvoice'
import { DataTable } from './DataTable'
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import InvoiceFinalizeAction from './InvoiceFinalizeAction';
import { AdvancedButton } from './AdvancedButton';
import { PATHS } from '../utils/shared/constants';

interface Props {
    ix: number,
    goodsOwner: GoodsOwner,
    invoices: OngoingInvoice[],
}


function InvoiceTable(props: Props) {
    const sortedInvoices = props.invoices.sort((a, b) => {
        return b.invoiceInfo.invoiceDate.localeCompare(a.invoiceInfo.invoiceDate);
    });

    const groupedPerMonth: Record<string, number> = {};
    for (const invoice of sortedInvoices) {
        const invoiceDate = new Date(invoice.invoiceInfo.invoiceDate);
        const inoviceMonthKey = invoiceDate.toLocaleString('default', { month: 'short', year: 'numeric' });;

        if (!groupedPerMonth[inoviceMonthKey]) {
            groupedPerMonth[inoviceMonthKey] = 0;
        }
        groupedPerMonth[inoviceMonthKey] += invoice.invoiceInfo.invoiceAmount;
    }


    const orderedMonthKeys = [];
    if (sortedInvoices.length > 0) {
        let currentDate = new Date(sortedInvoices[0].invoiceInfo.invoiceDate);
        currentDate.setDate(1);
        while (currentDate <= new Date()) {
            orderedMonthKeys.push(currentDate.toLocaleString('default', { month: 'short', year: 'numeric' }));
            currentDate.setMonth(currentDate.getMonth() + 1);
        }
    }

    const graphData = orderedMonthKeys.map(month => ({
        month,
        amount: groupedPerMonth[month] || 0
    }));



    const formatDate = (date: Date | undefined) => {
        if (!date) return 'Never';
        return new Date(date).toLocaleString();
    };

    const getStatusBadge = () => {
        if (props.goodsOwner.lastError) {
            return <span className="badge bg-danger ms-2" title={`Error: ${props.goodsOwner.lastError}`}>
                <i className="bi bi-exclamation-triangle me-1"></i>
                Error
            </span>;
        }
        if (props.goodsOwner.lastUpdateSuccess) {
            return <span className="badge bg-success ms-2" title={`Last success: ${formatDate(props.goodsOwner.lastUpdateSuccess)}`}>
                <i className="bi bi-check-circle me-1"></i>
                OK
            </span>;
        }
        return <span className="badge bg-secondary ms-2">
            <i className="bi bi-question-circle me-1"></i>
            Unknown
        </span>;
    };

    return <div>
        <div className='d-flex justify-content-between align-items-center '>
            <div className='d-flex align-items-center'>
                <h4 className='mb-0'>{props.goodsOwner.name} {'-> [BC]'} {props.goodsOwner.azureCustomerName}</h4>
                {getStatusBadge()}
            </div>
            <AdvancedButton icon='bi-gear' link={`${PATHS.SETTINGS}/${props.ix}`}>Settings</AdvancedButton>
        </div>

        {props.goodsOwner.lastError && (
            <div className="alert alert-danger alert-sm mt-2 mb-2">
                <small>
                    <strong>Last Error:</strong> {props.goodsOwner.lastError}
                    {props.goodsOwner.lastUpdateAttempt && (
                        <span className="ms-2 text-muted">
                            (Attempted: {formatDate(props.goodsOwner.lastUpdateAttempt)})
                        </span>
                    )}
                </small>
            </div>
        )}

        {!props.goodsOwner.lastError && props.goodsOwner.lastUpdateSuccess && (
            <div className="text-muted small mb-2">
                Last successful update: {formatDate(props.goodsOwner.lastUpdateSuccess)}
            </div>
        )}

        <ResponsiveContainer width="100%" height={100}>
            <AreaChart
                data={graphData}
            >
                <XAxis dataKey="month" />
                <Tooltip />
                <Area type="monotone" dataKey="amount" name='Invoice amounts' />

            </AreaChart>
        </ResponsiveContainer>

        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <DataTable data={sortedInvoices} columns={[
                {
                    label: 'Invoice',
                    render: (invoice: OngoingInvoice) => <>{invoice.invoiceInfo.title}</>,

                },
                {
                    label: 'Amount',
                    render: (invoice: OngoingInvoice) => <>{invoice.invoiceInfo.invoiceAmount}</>,

                },
                {
                    label: 'Invoice Date',
                    render: (invoice: OngoingInvoice) => <>{invoice.invoiceInfo.invoiceDate}</>,

                },
                {
                    label: 'Finalize',
                    render: (invoice: OngoingInvoice) => <InvoiceFinalizeAction goodsOwner={props.goodsOwner} invoice={invoice} />,
                },
            ]} />
        </div>
    </div>
}


export default InvoiceTable
